import { useEffect, useRef, useState, type TouchEvent } from "react";
import { createPortal } from "react-dom";

interface ImagePreviewModalProps {
  title?: string;
  images: string[];
  initialIndex?: number;
  onClose: () => void;
}

export default function ImagePreviewModal({
  title,
  images,
  initialIndex = 0,
  onClose,
}: ImagePreviewModalProps) {
  const normalizedIndex = Math.min(Math.max(initialIndex, 0), Math.max(images.length - 1, 0));
  const [current, setCurrent] = useState(normalizedIndex);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  useEffect(() => {
    setCurrent(normalizedIndex);
  }, [normalizedIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (images.length <= 1) {
        return;
      }
      if (event.key === "ArrowLeft") {
        setCurrent((value) => (value - 1 + images.length) % images.length);
      }
      if (event.key === "ArrowRight") {
        setCurrent((value) => (value + 1) % images.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, onClose]);

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (images.length <= 1 || touchStartXRef.current == null || touchStartYRef.current == null) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX < 0) {
      setCurrent((value) => (value + 1) % images.length);
      return;
    }

    setCurrent((value) => (value - 1 + images.length) % images.length);
  };

  if (!images.length) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex flex-col items-center justify-center"
      style={{ background: "rgba(0,0,0,0.92)" }}
      onClick={onClose}
    >
      <div className="absolute left-0 right-0 top-0 z-10 flex min-w-0 items-center justify-between gap-3 px-4 pb-3 pt-4">
        <span className="min-w-0 flex-1 truncate text-base font-bold text-white">{title || ""}</span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: "rgba(255,255,255,0.15)" }}
        >
          <i className="ri-close-line text-lg text-white" />
        </button>
      </div>
      <div
        className="relative flex items-center justify-center"
        style={{ width: "100%", maxWidth: 480 }}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img src={images[current]} alt={`${current + 1}`} className="w-full object-contain" style={{ maxHeight: "72vh" }} />
        {images.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => setCurrent((value) => (value - 1 + images.length) % images.length)}
              className="absolute left-3 flex h-8 w-8 items-center justify-center rounded-full"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <i className="ri-arrow-left-s-line text-lg text-white" />
            </button>
            <button
              type="button"
              onClick={() => setCurrent((value) => (value + 1) % images.length)}
              className="absolute right-3 flex h-8 w-8 items-center justify-center rounded-full"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <i className="ri-arrow-right-s-line text-lg text-white" />
            </button>
          </>
        ) : null}
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto px-4" style={{ scrollbarWidth: "none" }} onClick={(event) => event.stopPropagation()}>
        {images.map((image, index) => (
          <button
            key={`${image}-${index}`}
            type="button"
            onClick={() => setCurrent(index)}
            className="flex-shrink-0 overflow-hidden rounded-lg"
            style={{
              width: 56,
              height: 56,
              border: current === index ? "2px solid #FF8C3A" : "2px solid transparent",
              opacity: current === index ? 1 : 0.55,
            }}
          >
            <img src={image} alt="" className="h-full w-full object-cover object-top" />
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-white/50">
        {current + 1} / {images.length}
      </p>
    </div>,
    document.body,
  );
}
